import styled from "styled-components";
import { Icon } from "@vds/icons";
import { device } from "../../styles/breakpoints";

/**
 * Dashboard Responsive Styles
 *
 * Mobile-first approach using standard breakpoints:
 * - Mobile: 320px - 767px (default, no media query)
 * - Tablet: 768px - 1023px
 * - Desktop: 1024px - 1439px
 * - Large: 1440px+
 *
 * Old breakpoints REMOVED:
 * - 1133px → 1024px
 * - 1272px → 1024px
 * - 1680px → 1440px
 */

// ============================================
// MAIN LAYOUT COMPONENTS
// ============================================

export const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  overflow-x: hidden;

  /* Mobile (320-767px): Compact spacing, wrap if needed */
  gap: 8px;
  flex-wrap: wrap;

  /* Tablet (768-1399px): More spacing, no wrap */
  @media ${device.tablet} {
    gap: 12px;
    flex-wrap: nowrap;
  }

  /* Desktop (1400px+): Full spacing */
  @media ${device.desktop} {
    gap: 16px;
  }
`;

export const Container = styled.div`
  display: grid;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  overflow-x: hidden;

  /* Mobile (320-767px): Stacked - Events → Floor Plan → Widgets */
  grid-template-columns: 1fr;
  grid-template-areas:
    "column1"
    "column2"
    "column3";
  gap: 12px;

  /* Tablet Portrait (768-1399px): Stacked - Events → Floor Plan → Widgets */
  @media (min-width: 768px) and (max-width: 1399px) and (orientation: portrait) {
    grid-template-columns: 1fr;
    grid-template-areas:
      "column1"
      "column2"
      "column3";
    gap: 16px;
  }

  /* Tablet Landscape (768-1399px): Side-by-side - (Events | Floor Plan) → Widgets */
  @media (min-width: 768px) and (max-width: 1399px) and (orientation: landscape) {
    grid-template-columns: minmax(0, 2fr) minmax(0, 3fr);
    grid-template-areas:
      "column1 column2"
      "column3 column3";
    gap: 16px;
  }

  /* Desktop (1400-1439px): Side-by-side - (Events | Floor Plan) → Widgets */
  @media ${device.desktop} {
    grid-template-columns: minmax(0, 1fr) minmax(0, 2fr);
    grid-template-areas:
      "column1 column2"
      "column3 column3";
    gap: 24px;
  }

  /* Large (1440-1919px): Keep MacBook layout — centered with max-width cap */
  @media ${device.large} {
    max-width: 1600px;
    margin: 0 auto;
  }

  /* Widescreen (1920px+): 2-column layout — events | floor plan expand, widgets stay at bottom */
  @media ${device.widescreen} {
    max-width: none;
    margin: 0;
    grid-template-columns: minmax(0, 1fr) minmax(0, 3fr);
    grid-template-areas:
      "column1 column2"
      "column3 column3";
    gap: 32px;
  }
`;

export const VerticalContainer = styled.div`
  display: flex;
  width: 100%;
  gap: 12px;

  /* Mobile: Stack vertically */
  flex-direction: column;
  height: auto;

  /* Desktop: Horizontal layout */
  @media ${device.desktop} {
    flex-direction: row;
    height: 660px;
    gap: 24px;
  }
`;

// ============================================
// SITE TREE / LEFT CONTAINER (column1)
// ============================================

export const ContentContainer = styled.div`
  grid-area: column1;
  display: flex;
  flex-direction: column;
  border-radius: 8px;
  background: var(--vds-background-secondary-light, #f6f6f6);
  overflow: hidden;
  position: relative;
  box-sizing: border-box;
  width: 100%;
  max-width: 100%;

  /* Mobile (320-767px): Always expanded */
  max-height: none;
  height: auto;

  /* Tablet Portrait (768-1399px): Full height, always visible */
  @media (min-width: 768px) and (max-width: 1399px) and (orientation: portrait) {
    max-height: none;
    min-height: 300px;
    height: auto;
  }

  /* Tablet Landscape (768-1399px): Match floor plan height.
     height: auto (not 100%) - the grid row has no explicit height to
     resolve against, so 100% could under-size this relative to its
     actual content (the fixed-height event List), clipping tiles via overflow: hidden. */
  @media (min-width: 768px) and (max-width: 1399px) and (orientation: landscape) {
    max-height: none;
    min-height: 600px;
    height: auto;
  }

  /* Desktop (1400px+): Fixed width sidebar */
  @media ${device.desktop} {
    max-height: none;
    min-height: 600px;
    height: auto;
    max-width: 444px;
    min-width: 300px;
  }

  /* Large (1440px+): Slightly wider */
  @media ${device.large} {
    max-width: 500px;
  }

  /* Widescreen (1920px+): Remove cap — fills 1fr column */
  @media ${device.widescreen} {
    max-width: none;
    min-height: 700px;
  }
`;

export const LeftWidget = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  gap: 12px;
  flex-shrink: 0;
  border-radius: 8px;
  background: var(--vds-background-secondary-light, #f6f6f6);
  overflow: hidden;
  height: 100%;
  width: 100%;
  padding: 12px;

  /* Tablet: More padding */
  @media ${device.tablet} {
    gap: 16px;
    padding: 16px;
  }

  /* Desktop: Standard sizing */
  @media ${device.desktop} {
    gap: 24px;
    padding: 24px;
    max-width: 444px;
  }
`;

export const TextLinkCaretButton = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  position: sticky;
  margin-right: 10px;
  margin-top: 15px;
  cursor: pointer;
  min-height: 44px; /* Touch-friendly */

  @media ${device.tablet} {
    margin-bottom: 5px;
  }
`;

export const ScrollableContainer = styled.div`
  overflow-x: hidden;
  overflow-y: auto;
  transition: max-height 0.3s ease;
  height: auto;
  width: 100%;

  /* Mobile: Limited height when expanded */
  max-height: 350px;

  /* Tablet+: Auto height */
  @media ${device.tablet} {
    max-height: none;
  }
`;

export const EventsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
  position: relative;
  height: 100%;

  @media ${device.tablet} {
    gap: 16px;
  }

  @media ${device.desktop} {
    gap: 24px;
  }
`;

export const EventCardContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding-bottom: 20px;
  width: 100%;
  height: auto;
  position: relative;
  align-items: center;

  @media ${device.tablet} {
    gap: 16px;
  }
`;

export const NoCriticalEventText = styled.div`
  color: var(--vds-elements-text-icons-borders-primary-onlight, #000);
  font-family: Verizon NHG DS;
  font-size: 24px;
  font-style: normal;
  font-weight: 400;
  line-height: 28px;
  padding-top: 20px;

  /* Desktop: Larger text */
  @media ${device.desktop} {
    font-size: 32px;
    line-height: 36px;
  }
`;

// ============================================
// FLOOR PLAN / TOP CONTAINER (column2)
// ============================================

export const TopContainerWrapper = styled.div`
  grid-area: column2;
  background-color: #f6f6f6;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  position: relative;
  box-sizing: border-box;
  width: 100%;
  max-width: 100%;
  overflow-x: hidden; /* Prevent horizontal scroll */
  overflow-y: visible; /* Allow dropdown to show vertically */

  /* Mobile (320-767px): Compact */
  padding: 12px;
  gap: 12px;
  min-height: 350px;
  height: auto;

  /* Tablet Portrait (768-1399px): Larger for better view */
  @media (min-width: 768px) and (max-width: 1399px) and (orientation: portrait) {
    padding: 16px;
    gap: 16px;
    min-height: 500px;
    height: auto;
  }

  /* Tablet Landscape (768-1399px): Full height to match events */
  @media (min-width: 768px) and (max-width: 1399px) and (orientation: landscape) {
    padding: 16px;
    gap: 16px;
    min-height: 600px;
    height: 100%;
  }

  /* Desktop (1400px+): Spacious layout */
  @media ${device.desktop} {
    padding: 24px;
    gap: 24px;
    min-height: 680px;
  }

  /* Widescreen (1920px+): Fill column height */
  @media ${device.widescreen} {
    min-height: 700px;
    height: 100%;
  }
`;

export const FloorMapPlaceholder = styled.div`
  grid-area: column2;
  background-color: #f6f6f6;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  position: relative;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  overflow: hidden;
  isolation: isolate;
  min-height: 400px;
  height: auto;

  @media ${device.tablet} {
    min-height: 500px;
    gap: 16px;
  }

  @media ${device.desktop} {
    min-height: 680px;
    gap: 24px;
  }
`;

export const FloorMapContainer = styled.div`
  width: 100%;
  max-width: 100%;
  height: 100%;
  flex: 1;
  display: flex;
  min-height: 0;
  box-sizing: border-box;
  overflow: hidden; /* Prevent overflow */

  /* Mobile: Stack tabs on top, floor plan below */
  flex-direction: column;

  /* Desktop: Side-by-side (tabs left, floor plan right) */
  @media ${device.desktop} {
    flex-direction: row;
  }
`;

export const FloorPlanWrapper = styled.div`
  flex: 1;
  width: 100%;
  max-width: 100%;
  height: 100%;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden; /* Keep overflow hidden for canvas only */
  box-sizing: border-box;
  background: #f6f6f6; /* Match container background */
  ${({ $loading }) => $loading && "opacity: 0.5; transition: opacity 0.3s ease;"}

  /* Mobile (320-767px): Compact height, show full floor plan */
  @media (max-width: 767px) {
    height: 280px;
  }

  /* Tablet Portrait (768-1399px): Large view */
  @media (min-width: 768px) and (max-width: 1399px) and (orientation: portrait) {
    height: 600px;
    min-height: 500px;
  }

  /* Tablet Landscape (768-1399px): Balanced height */
  @media (min-width: 768px) and (max-width: 1399px) and (orientation: landscape) {
    height: 450px;
  }

  /* Desktop (1400px+): Full size */
  @media ${device.desktop} {
    height: 600px;
  }

  /* Widescreen (1920px+): Taller to fill available height */
  @media ${device.widescreen} {
    height: 650px;
  }
`;

export const FloorTabsContainer = styled.div`
  display: flex;
  width: 100%;

  /* Mobile: Horizontal scroll at top */
  flex-direction: row;
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
  flex-shrink: 0;
  height: auto;
  padding: 8px 0;
  gap: 8px;
  background: #fff;
  border-radius: 4px;
  margin-bottom: 8px;

  /* Hide scrollbar but keep functionality */
  scrollbar-width: thin;
  &::-webkit-scrollbar {
    height: 4px;
  }
  &::-webkit-scrollbar-track {
    background: #f1f1f1;
  }
  &::-webkit-scrollbar-thumb {
    background: #888;
    border-radius: 2px;
  }

  /* Tablet: Same as mobile but more spacing */
  @media ${device.tablet} {
    margin-bottom: 12px;
    padding: 12px 0;
  }

  /* Desktop: Vertical tabs on left */
  @media ${device.desktop} {
    flex-direction: column;
    overflow-x: hidden;
    overflow-y: auto;
    width: 20%;
    height: 100%;
    margin-bottom: 0;
    margin-right: 16px;
    padding: 0;
    gap: 0;
    background: transparent;
  }
`;

export const FloorTab = styled.div`
  display: flex;
  align-items: center;
  padding: 8px 16px;
  gap: 10px;
  cursor: pointer;
  min-height: 44px; /* Touch-friendly */
  flex-shrink: 0;
  transition: background 0.2s ease, border-color 0.2s ease;

  /* Mobile: Horizontal tabs with bottom border indicator */
  min-width: 120px;
  white-space: nowrap;
  border-bottom: 3px solid ${(props) => (props.isSelected ? "#000" : "transparent")};
  border-left: none;
  background: ${(props) => (props.isSelected ? "#f0f0f0" : "transparent")};
  border-radius: 4px 4px 0 0;

  &:hover {
    background: #f0f0f0;
  }

  /* Tablet: Same horizontal style */
  @media ${device.tablet} {
    min-width: 140px;
  }

  /* Desktop: Vertical tabs with left border indicator */
  @media ${device.desktop} {
    width: 100%;
    min-width: auto;
    height: 36px;
    min-height: 36px;
    border-bottom: none;
    border-left-style: inset;
    border-left-color: ${(props) => (props.isSelected ? "black" : "#f6f6f6")};
    border-left-width: ${(props) => (props.isSelected ? "medium" : "thin")};
    border-radius: 0;
    background: transparent;

    &:hover {
      background: transparent;
    }
  }
`;

export const FloorTabText = styled.div`
  display: block;
  text-align: left;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  cursor: pointer;

  /* Mobile: Shorter max-width */
  max-width: 80px;

  /* Desktop: Wider max-width */
  @media ${device.desktop} {
    max-width: 100px;
  }

  .floor-container_floor-name {
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

// ============================================
// WIDGETS CONTAINER (column3)
// ============================================

export const WidgetLayout = styled.div`
  grid-area: column3;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  flex-shrink: 0;
  border-radius: 8px;
  background: var(--vds-background-secondary-light, #f6f6f6);
  overflow: hidden;
  padding-bottom: 16px;
`;

export const WidgetHeader = styled.div`
  display: flex;
  padding: 12px;
  width: 100%;
  justify-content: space-between;
  align-items: center;

  @media ${device.tablet} {
    padding: 16px;
  }

  @media ${device.desktop} {
    padding: 20px 20px 0px 20px;
  }
`;

export const WidgetGridContainer = styled.div`
  display: grid;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;

  /* Mobile (320-767px): Single column, full width */
  grid-template-columns: minmax(0, 1fr);
  padding: 12px;
  gap: 12px;

  /* Tablet Portrait (768-1399px): 2 columns for better fit */
  @media (min-width: 768px) and (max-width: 1399px) and (orientation: portrait) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    padding: 16px;
    gap: 16px;
  }

  /* Tablet Landscape (768-1399px): 3 columns */
  @media (min-width: 768px) and (max-width: 1399px) and (orientation: landscape) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
    padding: 16px;
    gap: 16px;
  }

  /* Desktop (1400-1439px): 3 columns */
  @media ${device.desktop} {
    grid-template-columns: repeat(3, minmax(0, 1fr));
    padding: 20px;
    gap: 24px;
  }

  /* Large (1440px+): keep 3 columns — wider container means wider widgets */
  @media ${device.large} {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  /* Widescreen (1920px+): keep 3 columns — full-width row gives even wider widgets */
  @media ${device.widescreen} {
    grid-template-columns: repeat(3, minmax(0, 1fr));
    padding: 24px;
    gap: 24px;
  }
`;

export const WidgetWrapStyle = styled.div`
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  border-radius: 8px;
  background-color: #ffffff;
  overflow: hidden;

  /* Mobile (320-767px): Compact padding */
  padding: 12px 16px;
  min-height: 120px;

  /* Desktop (1400px+): Larger padding and fixed height */
  @media ${device.desktop} {
    height: 140px;
    padding: 16px 20px;
  }
`;

export const RightWidget = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  flex: 1 0 0;
  align-self: flex-start;

  @media ${device.tablet} {
    gap: 16px;
  }

  @media ${device.desktop} {
    gap: var(--space-6-x, 24px);
  }
`;

export const StyledPlusIconDiv = styled.div`
  display: flex;
  margin-top: auto;
  margin-bottom: auto;
  background-color: #eef1f1;
  border-radius: 50%;
  width: 44px; /* Touch-friendly */
  height: 44px;
  cursor: pointer;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s;

  &:hover {
    background-color: #dfe2e2;
  }
`;

// ============================================
// SHARED COMPONENTS
// ============================================

export const EventCountBadge = styled.div`
  min-width: 30px;
  height: 18px;
  padding: 0 8px;
  border-radius: 999px;
  border: 1.5px solid var(--orange-58, #ff8027);
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const EventCountBadgeText = styled.div`
  font-family: Verizon NHG eTX;
  font-size: 10px;
  font-weight: 400;
  line-height: 15px;
  text-align: center;
  color: var(--orange-58, #ff8027);
`;

export const AllSitesTotalNumber = styled.span`
  display: flex;
  justify-content: center;
  align-items: center;
  min-width: 24px;
  height: 18px;
  padding: 2px 8px;
  gap: 10px;
  margin-left: 7px;
  border-radius: 99px;
  border: 1.5px solid #ff8027;
  background: transparent;
  font-family: "Verizon NHG TX", sans-serif;
  color: #ff8027;
  font-size: 12px;
  font-weight: 400;
  line-height: 12px;
  text-align: center;
`;

export const StyledIcon = styled(Icon)`
  display: flex;
  margin-top: auto;
  margin-bottom: auto !important;
  width: 20px;
  height: 20px;
  margin-right: 7px;
`;

export const SparklineContainer = styled.div`
  margin-top: 8px;
  width: 75%;
  height: 64px;
  margin-left: 2px;
  border-radius: 6px;
  box-shadow: 0px 1px 10px 0px #0000001f;
`;

// ============================================
// MODAL COMPONENTS (Already responsive)
// ============================================

export const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100000;
  padding: 0;

  /* Tablet+: Add padding */
  @media ${device.tablet} {
    padding: 16px;
  }
`;

export const ModalContainer = styled.div`
  background: white;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  position: relative;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  overflow: hidden;

  /* Mobile: Full screen */
  width: 100%;
  height: 100%;
  max-height: 100%;
  border-radius: 0;

  /* Tablet+: Centered with max-width */
  @media ${device.tablet} {
    width: ${props => props.width || "560px"};
    height: ${props => props.$height || "auto"};
    max-height: 90vh;
    border-radius: 8px;
  }

  /* Large: Larger max-width */
  @media ${device.large} {
    max-width: ${props => props.$maxWidth || "800px"};
  }
`;

export const ModalBodyContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
  padding: 16px;

  @media ${device.tablet} {
    padding: 24px;
  }
`;

export const ScrollableContent = styled.div`
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  position: relative;
  -webkit-overflow-scrolling: touch;

  &:focus {
    outline: none;
  }

  /* Custom scrollbar (desktop only) */
  @media ${device.desktop} {
    &::-webkit-scrollbar {
      width: 8px;
    }

    &::-webkit-scrollbar-track {
      background: #f1f1f1;
    }

    &::-webkit-scrollbar-thumb {
      background: #888;
      border-radius: 4px;
    }

    &::-webkit-scrollbar-thumb:hover {
      background: #555;
    }
  }
`;

export const ModalActionsContainer = styled.div`
  padding: 16px;

  @media ${device.tablet} {
    padding: 20px 20px 0px 20px;
  }
`;

export const ModalBodyContent = styled.div`
  padding: 16px 0;
  overflow: auto;

  /* Mobile: Flexible height */
  max-height: none;

  /* Desktop: Limited height */
  @media ${device.desktop} {
    max-height: 41vh;
  }
`;

// ============================================
// DEPRECATED / UNUSED (Keep for compatibility)
// ============================================

export const WidgetOverlay = styled.div`
  width: 100%;
  height: 140px;
  background-color: #ffffff;
  position: absolute;
  z-index: 1;
  padding: 16px 20px;
  border-radius: 8px;
  opacity: 80%;
`;

export const WidgetEdit = styled.div`
  position: absolute;
  width: 17px;
  height: 140px;
  padding: 56px 0px;
  border-radius: 8px 0px 0px 8px;
  background: #d9d9d9;
  border: 1px solid var(--vds-color-elements-secondary, #6f7171);
  z-index: 1;
`;

export const BottomContainerWrapper = styled.div`
  width: 100%;
  max-width: 852px;
  height: auto;
  min-height: 300px;
  border-radius: 8px;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  gap: 12px;
  align-self: stretch;

  @media ${device.desktop} {
    height: 404px;
    gap: var(--space-6-x, 24px);
  }
`;
export const NewEventLink = styled.div`
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  top: 0.5em;
  display: flex;
  align-items: center;
  justify-content: center;
  &:hover #dashboard_new-eventDetected,
  &:hover button#dashboard_new-eventDetected {
    color: #fff;
    font-weight: 500;
  }

  &:hover #dashboard_new-eventDetected * {
    color: #fff;
    font-weight: 500;
  }
`;

export const TextLink = styled.a`
  background-color: gray;
  color: white;
  border-radius: 0.6em;
  padding: 0.3em
;`
